'use client';

import { useEffect, useState } from 'react';
import { Search, Loader2, RefreshCw, Eye, CheckCircle } from 'lucide-react';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency, formatNumber, formatDateTime, cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

interface Settlement {
  id: string;
  providerId: string;
  providerName: string;
  periodStart: string;
  periodEnd: string;
  totalRevenue: number;
  platformFee: number;
  providerShare: number;
  totalMessages: number;
  totalMinutes: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  paidAt: string | null;
  createdAt: string;
}

interface ProviderSettlement {
  id: string;
  providerId: string;
  providerName: string;
  period: string;
  grossRevenue: number;
  platformFeeRate: number;
  platformFee: number;
  providerAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'disputed';
  dueDate: string;
  paidAt: string | null;
  createdAt: string;
}

interface Payout {
  id: string;
  providerId: string;
  providerName: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  method: string;
  reference: string;
  processedAt: string | null;
  createdAt: string;
}

export function AdminSettlementsPage() {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [providerSettlements, setProviderSettlements] = useState<ProviderSettlement[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'provider' | 'payouts'>('overview');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processSettlement, setProcessSettlement] = useState<Settlement | null>(null);
  const [processing, setProcessing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settlementsRes, providerRes, payoutsRes] = await Promise.all([
        adminApi.getSettlements(`page=${currentPage}&limit=${itemsPerPage}&${search ? `search=${search}&` : ''}${statusFilter ? `status=${statusFilter}&` : ''}${periodFilter ? `period=${periodFilter}&` : ''}`),
        adminApi.getProviderSettlements(`page=1&limit=50`),
        adminApi.getPayouts(`page=1&limit=50`),
      ]);
      if (settlementsRes && typeof settlementsRes === 'object' && 'items' in settlementsRes) {
        setSettlements((settlementsRes as any).items);
      }
      if (providerRes && typeof providerRes === 'object' && 'items' in providerRes) {
        setProviderSettlements((providerRes as any).items);
      }
      if (payoutsRes && typeof payoutsRes === 'object' && 'items' in payoutsRes) {
        setPayouts((payoutsRes as any).items);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, search, statusFilter, periodFilter]);

  const handleProcess = async (settlement: Settlement) => {
    setProcessSettlement(settlement);
    setProcessDialogOpen(true);
  };

  const confirmProcess = async () => {
    if (!processSettlement) return;
    setProcessing(true);
    try {
      await adminApi.processSettlement(processSettlement.id);
      toast.success('Settlement processed');
      fetchData();
    } catch (err) {
      toast.error('Failed to process settlement');
    } finally {
      setProcessing(false);
      setProcessDialogOpen(false);
      setProcessSettlement(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge className="bg-amber-500/10 text-amber-400">Pending</Badge>;
      case 'processing': return <Badge className="bg-blue-500/10 text-blue-400 animate-pulse">Processing</Badge>;
      case 'completed': return <Badge className="bg-green-500/10 text-green-400">Completed</Badge>;
      case 'paid': return <Badge className="bg-green-500/10 text-green-400">Paid</Badge>;
      case 'failed': return <Badge className="bg-red-500/10 text-red-400">Failed</Badge>;
      case 'approved': return <Badge className="bg-green-500/10 text-green-400">Approved</Badge>;
      case 'disputed': return <Badge className="bg-red-500/10 text-red-400">Disputed</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading && currentPage === 1) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="glass">
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Settlements</h1>
          <p className="text-slate-400 mt-1">Manage provider settlements and payouts</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as any)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 bg-slate-800/50">
          <TabsTrigger value="overview">Daily Settlements</TabsTrigger>
          <TabsTrigger value="provider">Provider Settlements</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="glass">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle>Daily Settlement Records</CardTitle>
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} className="pl-10" />
                  </div>
                  <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={periodFilter} onValueChange={v => { setPeriodFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Periods</SelectItem>
                      <SelectItem value="2024-01">Jan 2024</SelectItem>
                      <SelectItem value="2024-02">Feb 2024</SelectItem>
                      <SelectItem value="2024-03">Mar 2024</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Platform Fee</TableHead>
                    <TableHead>Provider Share</TableHead>
                    <TableHead>Messages</TableHead>
                    <TableHead>Minutes</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Paid At</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {settlements.length > 0 ? (
                    settlements.map(settlement => (
                      <TableRow key={settlement.id}>
                        <TableCell>{settlement.periodStart} to {settlement.periodEnd}</TableCell>
                        <TableCell>{settlement.providerName}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(settlement.totalRevenue)}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(settlement.platformFee)}</TableCell>
                        <TableCell className="font-mono text-green-400">{formatCurrency(settlement.providerShare)}</TableCell>
                        <TableCell>{formatNumber(settlement.totalMessages)}</TableCell>
                        <TableCell>{formatNumber(settlement.totalMinutes)}</TableCell>
                        <TableCell>{getStatusBadge(settlement.status)}</TableCell>
                        <TableCell className="text-slate-400">{settlement.paidAt ? formatDateTime(settlement.paidAt) : '-'}</TableCell>
                        <TableCell>
                          {settlement.status === 'pending' && (
                            <Button variant="ghost" size="icon" className="text-green-400 hover:text-green-300" onClick={() => handleProcess(settlement)}>
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="text-cyan-400 hover:text-cyan-300">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-400">No settlements found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="provider">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Provider Monthly Settlements</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Gross Revenue</TableHead>
                    <TableHead>Fee Rate</TableHead>
                    <TableHead>Platform Fee</TableHead>
                    <TableHead>Provider Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Paid At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providerSettlements.length > 0 ? (
                    providerSettlements.map(s => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.providerName}</TableCell>
                        <TableCell>{s.period}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(s.grossRevenue)}</TableCell>
                        <TableCell>{(s.platformFeeRate * 100).toFixed(1)}%</TableCell>
                        <TableCell className="font-mono">{formatCurrency(s.platformFee)}</TableCell>
                        <TableCell className="font-mono text-green-400">{formatCurrency(s.providerAmount)}</TableCell>
                        <TableCell>{getStatusBadge(s.status)}</TableCell>
                        <TableCell className="text-slate-400">{formatDateTime(s.dueDate)}</TableCell>
                        <TableCell className="text-slate-400">{s.paidAt ? formatDateTime(s.paidAt) : '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-400">No provider settlements found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Provider Payouts</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Processed At</TableHead>
                    <TableHead>Created At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.length > 0 ? (
                    payouts.map(p => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.providerName}</TableCell>
                        <TableCell className="font-mono text-green-400">{formatCurrency(p.amount)}</TableCell>
                        <TableCell>{p.method}</TableCell>
                        <TableCell className="font-mono text-sm">{p.reference}</TableCell>
                        <TableCell>{getStatusBadge(p.status)}</TableCell>
                        <TableCell className="text-slate-400">{p.processedAt ? formatDateTime(p.processedAt) : '-'}</TableCell>
                        <TableCell className="text-slate-400">{formatDateTime(p.createdAt)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-400">No payouts found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Settlement</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">Process settlement for <strong>{processSettlement?.providerName}</strong> period <strong>{processSettlement?.periodStart} to {processSettlement?.periodEnd}</strong>?</p>
            <div className="mt-4 p-4 bg-slate-800/50 rounded-xl">
              <p className="text-sm text-slate-400">Provider Share: <strong className="text-green-400">{processSettlement ? formatCurrency(processSettlement.providerShare) : ''}</strong></p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmProcess} disabled={processing}>
              {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Process'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
